import React, { useState, useEffect } from 'react';
import { COLORS } from '../../../../utils/constants';
import { adminAPI } from '../../../../services/api';
import { 
  ModalWrapper, 
  FormInput, 
  FormSelect, 
  ErrorMessage, 
  SubmitButton,
  TabButton,
  ToggleSwitch
} from './TeamUIComponents';

// Create Team Member Modal
export const CreateTeamMemberModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'manager',
    parent_admin_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [admins, setAdmins] = useState([]);

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      const res = await adminAPI.getAdminsList();
      setAdmins(res.data || []);
    } catch (err) {
      console.error('Failed to load admins:', err);
    }
  };

  const handleSubmit = async () => {
    if (!form.email || !form.password || !form.name) {
      setError('Заполните все поля');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await adminAPI.createTeamMember({
        ...form,
        parent_admin_id: form.role === 'manager' ? form.parent_admin_id : null
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка создания');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper title="Новый сотрудник" onClose={onClose}>
      {error && <ErrorMessage message={error} />}
      <div style={{ display: 'grid', gap: '12px' }}>
        <FormInput label="Имя" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <FormInput label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
        <FormInput label="Пароль" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
        <FormSelect
          label="Роль"
          value={form.role}
          onChange={(v) => setForm({ ...form, role: v })}
          options={[
            { value: 'manager', label: 'Менеджер' },
            { value: 'admin', label: 'Админ' },
            { value: 'support', label: 'Поддержка' }
          ]}
        />
        {form.role === 'manager' && (
          <FormSelect
            label="Привязать к админу"
            value={form.parent_admin_id}
            onChange={(v) => setForm({ ...form, parent_admin_id: v })}
            options={[
              { value: '', label: 'Не привязан' },
              ...admins.map(a => ({ value: a.id, label: a.name || a.email }))
            ]}
          />
        )}
      </div>
      <SubmitButton onClick={handleSubmit} loading={loading} label="Создать" />
    </ModalWrapper>
  );
};

// Edit Team Member Modal
export const EditTeamMemberModal = ({ member, onClose, onSuccess }) => {
  const defaultPermissions = {
    clients: {
      view: true,
      edit: true,
      assign: true,
      change_status: true,
      add_tags: true,
      send_email: true,
      view_history: true,
    },
    requests: {
      view: true,
      approve_deposit: true,
      reject_deposit: true,
      approve_withdrawal: true,
      reject_withdrawal: true,
    },
    positions: {
      view: true,
      create: true,
      edit: true,
      close: true,
    },
    support: {
      view: true,
      reply: true,
      close: true,
    },
    team: {
      view: true,
      edit: false,
      delete: false,
    },
    ido: {
      view: true,
      create: false,
      edit: false,
      delete: false,
      manage_client_ido: true,
    },
  };

  const mergePermissions = (saved, defaults) => {
    const merged = {};
    for (const section in defaults) {
      merged[section] = {};
      for (const perm in defaults[section]) {
        merged[section][perm] = saved?.[section]?.[perm] ?? defaults[section][perm];
      }
    }
    return merged;
  };

  const [form, setForm] = useState({
    email: member.email || '',
    password: '',
    name: member.name || '',
    parent_admin_id: member.parent_admin_id || '',
    is_active: member.is_active !== false,
    permissions: mergePermissions(member.permissions, defaultPermissions),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [admins, setAdmins] = useState([]);
  const [activeTab, setActiveTab] = useState('general');
  const [activePermSection, setActivePermSection] = useState('clients');

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      const res = await adminAPI.getAdminsList();
      setAdmins(res.data || []);
    } catch (err) {
      console.error('Failed to load admins:', err);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      const updateData = {
        name: form.name || undefined,
        email: form.email !== member.email ? form.email : undefined,
        password: form.password || undefined,
        parent_admin_id: (member.role === 'manager' || member.role === 'admin') ? (form.parent_admin_id || null) : undefined,
        is_active: form.is_active,
        permissions: form.permissions,
      };
      
      Object.keys(updateData).forEach(key => 
        updateData[key] === undefined && delete updateData[key]
      );
      
      await adminAPI.updateTeamMember(member.id, updateData);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка обновления');
    } finally {
      setLoading(false);
    }
  };

  const permissionSections = {
    clients: { label: '👥 Клиенты', icon: '👥' },
    requests: { label: '📋 Заявки', icon: '📋' },
    positions: { label: '📈 Позиции', icon: '📈' },
    support: { label: '💬 Поддержка', icon: '💬' },
    team: { label: '👔 Команда', icon: '👔' },
    ido: { label: '🚀 IDO', icon: '🚀' },
  };

  const permissionLabels = {
    clients: {
      view: 'Просмотр списка клиентов',
      edit: 'Редактирование данных клиента',
      assign: 'Назначение клиентов на сотрудников',
      change_status: 'Изменение статуса клиента',
      add_tags: 'Добавление/удаление тегов',
      send_email: 'Отправка email клиентам',
      view_history: 'Просмотр истории клиента',
    },
    requests: {
      view: 'Просмотр заявок',
      approve_deposit: 'Одобрение депозитов',
      reject_deposit: 'Отклонение депозитов',
      approve_withdrawal: 'Одобрение выводов',
      reject_withdrawal: 'Отклонение выводов',
    },
    positions: {
      view: 'Просмотр позиций',
      create: 'Создание позиций',
      edit: 'Редактирование позиций',
      close: 'Закрытие позиций',
    },
    support: {
      view: 'Просмотр тикетов',
      reply: 'Ответ на тикеты',
      close: 'Закрытие тикетов',
    },
    team: {
      view: 'Просмотр команды',
      edit: 'Редактирование сотрудников',
      delete: 'Удаление сотрудников',
    },
    ido: {
      view: 'Просмотр IDO проектов',
      create: 'Создание IDO',
      edit: 'Редактирование IDO',
      manage_client_ido: 'Управление IDO клиентов',
    },
  };

  const togglePermission = (section, perm) => {
    setForm({
      ...form,
      permissions: {
        ...form.permissions,
        [section]: {
          ...form.permissions[section],
          [perm]: !form.permissions[section][perm]
        }
      }
    });
  };

  const toggleAllInSection = (section, value) => {
    const newPerms = {};
    for (const perm in form.permissions[section]) {
      newPerms[perm] = value;
    }
    setForm({
      ...form,
      permissions: {
        ...form.permissions,
        [section]: newPerms
      }
    });
  };

  const countActivePerms = (section) => {
    const perms = form.permissions[section] || {};
    return Object.values(perms).filter(v => v).length;
  };

  const countTotalPerms = (section) => {
    return Object.keys(form.permissions[section] || {}).length;
  };

  return (
    <ModalWrapper title={`Редактирование: ${member.name || member.email}`} onClose={onClose} wide>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <TabButton active={activeTab === 'general'} onClick={() => setActiveTab('general')} label="Основное" />
        <TabButton active={activeTab === 'permissions'} onClick={() => setActiveTab('permissions')} label="Права доступа" />
      </div>

      {error && <ErrorMessage message={error} />}

      {activeTab === 'general' ? (
        <div style={{ display: 'grid', gap: '12px' }}>
          <FormInput label="Имя" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <FormInput label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <FormInput label="Новый пароль" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} placeholder="Оставьте пустым" />
          {(member.role === 'manager' || member.role === 'admin') && (
            <FormSelect
              label={member.role === 'admin' ? "Закрепить под админом" : "Привязать к админу"}
              value={form.parent_admin_id}
              onChange={(v) => setForm({ ...form, parent_admin_id: v })}
              options={[
                { value: '', label: member.role === 'admin' ? 'Независимый админ (верхний уровень)' : 'Не привязан' },
                ...admins.filter(a => a.id !== member.id).map(a => ({ value: a.id, label: a.name || a.email }))
              ]}
            />
          )}
          {member.role === 'admin' && form.parent_admin_id && (
            <div style={{ 
              padding: '10px 12px', 
              backgroundColor: `${COLORS.warning}15`, 
              borderRadius: '8px',
              border: `1px solid ${COLORS.warning}40`,
              fontSize: '12px',
              color: COLORS.warning
            }}>
              ⚠️ Этот админ будет видеть только своих клиентов. Родительский админ будет видеть всех клиентов этого админа.
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
            <span style={{ color: COLORS.textSecondary, fontSize: '14px' }}>Статус:</span>
            <button
              onClick={() => setForm({ ...form, is_active: !form.is_active })}
              style={{
                padding: '6px 12px',
                backgroundColor: form.is_active ? `${COLORS.success}20` : `${COLORS.danger}20`,
                border: `1px solid ${form.is_active ? COLORS.success : COLORS.danger}`,
                borderRadius: '6px',
                color: form.is_active ? COLORS.success : COLORS.danger,
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {form.is_active ? 'Активен' : 'Заблокирован'}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
            {Object.entries(permissionSections).map(([key, { label }]) => (
              <button
                key={key}
                onClick={() => setActivePermSection(key)}
                style={{
                  padding: '8px 14px',
                  backgroundColor: activePermSection === key ? COLORS.primary : COLORS.background,
                  border: `1px solid ${activePermSection === key ? COLORS.primary : COLORS.border}`,
                  borderRadius: '8px',
                  color: activePermSection === key ? '#000' : COLORS.textSecondary,
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {label}
                <span style={{
                  backgroundColor: activePermSection === key ? 'rgba(0,0,0,0.2)' : COLORS.card,
                  padding: '2px 6px',
                  borderRadius: '10px',
                  fontSize: '10px',
                }}>
                  {countActivePerms(key)}/{countTotalPerms(key)}
                </span>
              </button>
            ))}
          </div>
          
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button
              onClick={() => toggleAllInSection(activePermSection, true)}
              style={{
                padding: '6px 12px',
                backgroundColor: COLORS.success,
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              ✓ Включить все
            </button>
            <button
              onClick={() => toggleAllInSection(activePermSection, false)}
              style={{
                padding: '6px 12px',
                backgroundColor: COLORS.danger,
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              ✕ Выключить все
            </button>
          </div>

          <div style={{ display: 'grid', gap: '8px' }}>
            {Object.entries(permissionLabels[activePermSection] || {}).map(([key, label]) => (
              <div key={key} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 14px',
                backgroundColor: form.permissions[activePermSection]?.[key] ? `${COLORS.success}10` : COLORS.background,
                border: `1px solid ${form.permissions[activePermSection]?.[key] ? `${COLORS.success}30` : COLORS.border}`,
                borderRadius: '8px',
                transition: 'all 0.2s ease',
              }}>
                <span style={{ color: COLORS.text, fontSize: '13px' }}>{label}</span>
                <ToggleSwitch
                  value={form.permissions[activePermSection]?.[key] || false}
                  onChange={() => togglePermission(activePermSection, key)}
                />
              </div>
            ))}
          </div>
          
          <div style={{ marginTop: '16px', padding: '12px', backgroundColor: COLORS.background, borderRadius: '8px' }}>
            <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '8px', fontWeight: '600' }}>СВОДКА ПРАВ</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {Object.entries(permissionSections).map(([key, { icon }]) => {
                const active = countActivePerms(key);
                const total = countTotalPerms(key);
                const percent = Math.round((active / total) * 100);
                return (
                  <div key={key} style={{
                    padding: '6px 10px',
                    backgroundColor: percent === 100 ? `${COLORS.success}20` : percent === 0 ? `${COLORS.danger}20` : COLORS.card,
                    borderRadius: '6px',
                    fontSize: '11px',
                    color: COLORS.text,
                  }}>
                    {icon} {active}/{total}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <SubmitButton onClick={handleSubmit} loading={loading} label="Сохранить" />
    </ModalWrapper>
  );
};

// Delete Confirmation Modal
export const DeleteConfirmationModal = ({ member, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper title="Подтверждение удаления" onClose={onClose}>
      <div style={{ marginBottom: '20px' }}>
        <p style={{ color: COLORS.text, fontSize: '14px', marginBottom: '12px' }}>
          Вы уверены, что хотите удалить сотрудника?
        </p>
        <div style={{
          padding: '12px',
          backgroundColor: COLORS.background,
          borderRadius: '8px',
          border: `1px solid ${COLORS.border}`
        }}>
          <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '600' }}>
            {member.name || member.email}
          </div>
          <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
            {member.email}
          </div>
        </div>
        <div style={{
          marginTop: '12px',
          padding: '10px',
          backgroundColor: `${COLORS.danger}15`,
          borderRadius: '6px',
          border: `1px solid ${COLORS.danger}40`
        }}>
          <p style={{ color: COLORS.danger, fontSize: '12px', margin: 0 }}>
            ⚠️ Это действие нельзя отменить. Все клиенты этого сотрудника будут откреплены.
          </p>
        </div>
      </div>
      
      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={onClose}
          disabled={loading}
          style={{
            flex: 1,
            padding: '12px',
            backgroundColor: COLORS.background,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            color: COLORS.text,
            fontSize: '14px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          Отмена
        </button>
        <button
          onClick={handleConfirm}
          disabled={loading}
          style={{
            flex: 1,
            padding: '12px',
            backgroundColor: loading ? COLORS.border : COLORS.danger,
            border: 'none',
            borderRadius: '8px',
            color: loading ? COLORS.textSecondary : '#fff',
            fontSize: '14px',
            fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Удаление...' : 'Удалить'}
        </button>
      </div>
    </ModalWrapper>
  );
};
